import React, { useState, useEffect, useMemo, useRef } from 'react';
import { getConfigValue } from '../utils/config';

const DEFAULT_CAMERA_STREAM_BASE = 'http://devserv01.holdingthedrones.com:8081';
const HLS_SCRIPT_URL = 'https://cdn.jsdelivr.net/npm/hls.js@1.5.20/dist/hls.min.js';
const BOX_STREAM_OVERRIDES = {
  1: 'http://devserv01.holdingthedrones.com:8081/droneoperationcentre2/8022f0435c744d34a7c3e9908f9060dd/v0/master.m3u8'
};

let hlsScriptPromise;

const getCameraStreamBase = () => {
  const configuredBase = getConfigValue('REACT_APP_CAMERA_STREAM_BASE');
  return (configuredBase || DEFAULT_CAMERA_STREAM_BASE).replace(/\/$/, '');
};

const isHlsStream = (url) => /\.m3u8(?:$|\?)/i.test(url);

const loadHlsScript = () => {
  if (window.Hls) {
    return Promise.resolve(window.Hls);
  }

  if (!hlsScriptPromise) {
    hlsScriptPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = HLS_SCRIPT_URL;
      script.async = true;
      script.onload = () => resolve(window.Hls);
      script.onerror = () => reject(new Error('Unable to load HLS player.'));
      document.head.appendChild(script);
    });
  }

  return hlsScriptPromise;
};

const buildCameraStreamUrl = (boxId) => {
  if (!boxId) {
    return '';
  }

  const overriddenStreamUrl = BOX_STREAM_OVERRIDES[String(boxId).trim()];

  if (overriddenStreamUrl) {
    return overriddenStreamUrl;
  }

  const streamBase = getCameraStreamBase();

  if (window.location.protocol === 'https:' && streamBase.startsWith('http://')) {
    return '';
  }

  return `${streamBase}/?action=stream&box=${encodeURIComponent(boxId)}`;
};

const getBoxId = (box) => box?.DHTBOX_id || box?.DHTBOXid || box?.boxId || box?.id;

const CameraPlayer = ({ streamUrl, title, isFullscreen = false }) => {
  const videoRef = useRef(null);

  useEffect(() => {
    const video = videoRef.current;

    if (!video || !streamUrl) {
      return undefined;
    }

    let hls;
    let isCancelled = false;

    if (isHlsStream(streamUrl) && !video.canPlayType('application/vnd.apple.mpegurl')) {
      loadHlsScript()
        .then((Hls) => {
          if (isCancelled || !Hls?.isSupported()) {
            return;
          }

          hls = new Hls();
          hls.loadSource(streamUrl);
          hls.attachMedia(video);
        })
        .catch((error) => {
          console.error(error);
        });
    } else {
      video.src = streamUrl;
    }

    return () => {
      isCancelled = true;

      if (hls) {
        hls.destroy();
      }

      video.removeAttribute('src');
      video.load();
    };
  }, [streamUrl]);

  return (
    <video
      ref={videoRef}
      title={title}
      controls
      autoPlay
      muted
      playsInline
      style={{
        width: '100%',
        height: '100%',
        objectFit: isFullscreen ? 'contain' : 'cover',
        backgroundColor: '#000'
      }}
      onClick={(e) => e.stopPropagation()}
    />
  );
};

const CameraStream = ({ boxes = [] }) => {
  const boxOptions = useMemo(
    () =>
      boxes
        .map((box) => {
          const boxId = getBoxId(box);

          return {
            id: String(boxId || '').trim(),
            label: `Box ${boxId}`
          };
        })
        .filter((box) => box.id),
    [boxes]
  );

  const [selectedBoxId, setSelectedBoxId] = useState(boxOptions[0]?.id || '');
  const [streamUrl, setStreamUrl] = useState('');
  const [isFullscreen, setIsFullscreen] = useState(false);

  useEffect(() => {
    if (!boxOptions.length) {
      setSelectedBoxId('');
      return;
    }

    const hasSelectedBox = boxOptions.some((box) => box.id === selectedBoxId);

    if (!hasSelectedBox) {
      setSelectedBoxId(boxOptions[0]?.id || '');
    }
  }, [boxOptions, selectedBoxId]);

  useEffect(() => {
    if (selectedBoxId) {
      setStreamUrl(buildCameraStreamUrl(selectedBoxId));
    } else {
      setStreamUrl('');
    }
  }, [selectedBoxId]);

  return (
    <>
      <div className="card home-card h-100">
        <div className="card-body p-3 d-flex flex-column">
          <h6 className="card-title text-center mb-3">Camera Stream</h6>
          <div className="mb-3">
            <label className="form-label small mb-1 opacity-75">Select Box</label>
            <select
              className="form-control home-input"
              value={selectedBoxId}
              onChange={(e) => setSelectedBoxId(e.target.value)}
            >
              <option value="">-- Choose a box --</option>
              {boxOptions.map((box) => (
                <option key={box.id} value={box.id}>
                  {box.label}
                </option>
              ))}
            </select>
          </div>
          <div 
            className="flex-grow-1 d-flex align-items-center justify-content-center overflow-hidden rounded bg-dark" 
            style={{ minHeight: 0, cursor: 'pointer' }}
            onClick={() => setIsFullscreen(true)}
          >
            {selectedBoxId && streamUrl ? (
              <CameraPlayer
                streamUrl={streamUrl}
                title={`Camera Stream - Box ${selectedBoxId}`}
              />
            ) : (
              <div className="text-center text-white-50">
                <i className="bi bi-camera-video fs-1 d-block mb-2"></i>
                <p className="mb-0 small">Select a box to view camera stream</p>
              </div>
            )}
          </div>
          <small className="text-center text-muted mt-2">Click to expand</small>
        </div>
      </div>

      {isFullscreen && (
        <div 
          className="position-fixed top-0 start-0 w-100 h-100 bg-dark d-flex flex-column"
          style={{ zIndex: 9999 }}
          onClick={() => setIsFullscreen(false)}
        >
          <div className="d-flex justify-content-between align-items-center p-3 border-bottom border-secondary">
            <h5 className="text-white mb-0">Camera Stream - Box {selectedBoxId}</h5>
            <button 
              type="button"
              className="btn btn-outline-light"
              onClick={() => setIsFullscreen(false)}
            >
              <i className="bi bi-x-lg"></i> Close
            </button>
          </div>
          <div className="flex-grow-1 d-flex align-items-center justify-content-center overflow-hidden" onClick={(e) => e.stopPropagation()}>
            {selectedBoxId && streamUrl && (
              <CameraPlayer
                streamUrl={streamUrl}
                title={`Camera Stream Fullscreen - Box ${selectedBoxId}`}
                isFullscreen
              />
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default CameraStream;
